import { useCallback, useRef, useState, useLayoutEffect } from "react";
import { useTemplateEditorStore } from "../../../../store/useTemplateEditorStore";
import { useTemplatesStatusStore } from "../../../../store/useTemplatesStatusStore";
import {
  resolveColorToBrandkitColorIfNeeded,
  isGradient,
} from "../../../../helpers/itemsHelper";
import { ITEM_TYPES, Position, Size, TabKeys } from "../../../../types";
import { Header } from "./Header";
import { Logo } from "./Logo";
import { DraggableComponent } from "./DraggableComponent";
import { isTransparentHex } from "../../../../helpers";
import { useTemplateData } from "../../../../hooks/useTemplateData";

interface EditableLogoProps {
  previewWidth: number;
  previewHeight: number;
}

export const EditableLogo = ({
  previewWidth,
  previewHeight,
}: EditableLogoProps) => {
  const { logo, updateLogo, header } = useTemplateEditorStore((store) => ({
    logo: store.templateState.logo,
    updateLogo: store.updateLogo,
    header: store.templateState.header,
  }));

  const headerElementRef = useRef<HTMLDivElement>(null);
  const [bounds, setBounds] = useState<HTMLDivElement | undefined>(undefined);

  const {
    brandKitData: { brandKitPalette },
    placeholderAssets,
  } = useTemplateData();

  const resolvedHeaderBackground = resolveColorToBrandkitColorIfNeeded(
    header.background,
    brandKitPalette
  );
  const hasPicture = header.view === "Picture" && !!header.banner?.assetId;
  const hasHeader =
    hasPicture ||
    isGradient(resolvedHeaderBackground) ||
    (!!resolvedHeaderBackground && !isTransparentHex(resolvedHeaderBackground));

  useLayoutEffect(() => {
    if (hasHeader && headerElementRef.current) {
      setBounds(headerElementRef.current);
    } else {
      setBounds(undefined);
    }
  }, [hasHeader]);

  const { activeTab, setActiveTab } = useTemplatesStatusStore((state) => ({
    activeTab: state.activeTab,
    setActiveTab: state.setActiveTab,
  }));

  const onDragLogo = useCallback(
    ({ position, size }: { position?: Position; size?: Size }) => {
      updateLogo({
        position: position
          ? {
              x: position.x,
              y: position.y,
            }
          : logo.position,
        size: size
          ? {
              width: size.width,
              height: size.height,
            }
          : logo.size,
      });
    },
    [updateLogo, logo.position, logo.size]
  );

  return (
    logo.showLogo &&
    logo.assetId && (
      <>
        <Header
          headerItem={header}
          previewWidth={previewWidth}
          previewHeight={previewHeight}
          ref={headerElementRef}
        />
        <DraggableComponent
          position={logo.position}
          size={logo.size}
          rotation={logo.rotation}
          layerIndex={logo.layerIndex}
          item={{
            type: ITEM_TYPES.Logo,
          }}
          previewWidth={previewWidth}
          previewHeight={previewHeight}
          drag={true}
          resize={true}
          saveRatio={true}
          onDragStop={onDragLogo}
          onRotate={(rotation) => updateLogo({ rotation })}
          onFocus={() => {
            if (activeTab !== TabKeys.brand) {
              setActiveTab(TabKeys.brand);
            }
          }}
          bounds={bounds}
        >
          <Logo
            logo={logo}
            previewWidth={previewWidth}
            previewHeight={previewHeight}
            placeholderAssets={placeholderAssets}
          />
        </DraggableComponent>
      </>
    )
  );
};
